import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import MultiSelect from 'react-multi-select-component';
import _ from 'lodash';

import cards from '../../assets/data/card-colors.json';
import SubscriptionCard from './SubscriptionCard';

const META_TEMPLATE = {
  amount_color: '',
  card_bg_top_color: '',
  card_bg_bottom_color: '',
  card_title_color: '',
  left_button_bg_color: '',
  left_button_title_color: '',
  right_button_bg_color: '',
  right_button_title_color: '',
  bg_circle_top_left_color: '',
  bg_circle_bottom_right_color: '',
  summary_color: '',
};

const Validation = Yup.object().shape({
  name: Yup.string().trim().required('Required'),
  months: Yup.number().typeError('Invalid').min(0, '>= 0').required('Required'),
  price: Yup.number().typeError('Invalid').min(0, '>= 0').required('Required'),
  includes: Yup.array().of(Yup.string().trim()),
  meta: Yup.object().shape(
    Object.keys(META_TEMPLATE).reduce((acc, k) => {
      // allow empty or valid color (browser will supply hex via <input type="color">)
      acc[k] = Yup.string();
      return acc;
    }, {})
  ),
});

function mapParentFeatures(initialValues) {
  // converts [{feature:{id,name}, id:<pivot_id>}, ...] -> array with {feature_id, id, name}
  const parentFeatures = (initialValues?.features ?? []).map((data) => {
    const feat = { ...(data.feature || {}) };
    return { ...data, feature_id: feat.id, name: feat.name };
  });
  return parentFeatures;
}

export default function CreateSubscription({
  subscriptionInfo,
  accessMode,
  features_list,
  mode: pagemode,
}) {
  const isEdit = subscriptionInfo.mode === 'EDIT';
  const [awsKeys, setAwsKeys] = useState({});
  const [disableUpload, setDisableUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [showExistingCard, setShowExistingCard] = useState(false);
  const [existingCard, setExistingCard] = useState({ title: '', meta: {} });

  // Compose initial values
  const initialFormikValues = useMemo(() => {
    if (isEdit && subscriptionInfo.initialValues) {
      const iv = { ...subscriptionInfo.initialValues };
      const meta = { ...META_TEMPLATE, ...(iv.meta || {}) };
      const includes = Array.isArray(iv.includes) ? iv.includes : [];
      const features =
        (iv.features || []).map(({ feature }) => ({ label: feature.name, value: feature.id })) || [];
      return {
        id: iv.id,
        name: iv.name || '',
        months: iv.months ?? '',
        price: iv.price ?? '',
        icon: iv.icon || '',
        includes,
        meta,
        features,
      };
    }
    return {
      id: undefined,
      name: '',
      months: '',
      price: '',
      icon: '',
      includes: [],
      meta: { ...META_TEMPLATE },
      features: [],
    };
  }, [isEdit, subscriptionInfo.initialValues]);

  // keep photoUploaded true for edit mode (existing icon)
  useEffect(() => {
    if (isEdit) setPhotoUploaded(true);
  }, [isEdit]);

  const parentFeatures = useMemo(() => mapParentFeatures(subscriptionInfo.initialValues), [subscriptionInfo.initialValues]);

  const handleFeaturesChange = async (newFeatures, currentValues, setFieldValue) => {
    setFieldValue('features', newFeatures);

    // Only call add/delete if a subscription already exists (i.e., edit mode with id)
    if (!currentValues.id) return;

    const { deleteSubscriptionFeature, addSubscriptionFeature } = subscriptionInfo;

    // parentFeatures includes pivot ids & original mapping
    const before = parentFeatures; // [{id: pivot_id, feature_id, name}, ...]
    const afterIds = newFeatures.map((f) => f.value);

    // Added?
    const added = afterIds.find((fid) => !before.find((pf) => pf.feature_id === fid));
    if (added) {
      await addSubscriptionFeature({ subscription_id: currentValues.id, feature_id: added });
      return;
    }

    // Removed?
    const removed = before.find((pf) => !afterIds.includes(pf.feature_id));
    if (removed) {
      await deleteSubscriptionFeature(removed.id);
    }
  };

  const onFileChange = async (e, setFieldValue) => {
    setPhotoUploaded(false);
    setDisableUpload(false);

    const file = e.target.files?.[0];
    if (!file) return;

    const getAwsKey = subscriptionInfo.getAwsKey;
    const fileName = file.name.replace(/ /g, '_');
    const keys = await getAwsKey({ key: `subscriptions/images/${fileName}` });

    // Build icon URL now so it can be submitted later
    const iconUrl = `${keys.action}${keys.key}`;
    setAwsKeys(keys);
    setDisableUpload(true);
    setFieldValue('icon', iconUrl);
  };

  const uploadPhoto = async (evt) => {
    evt.preventDefault();
    if (!awsKeys.action) {
      toast.error('Please try again.');
      return;
    }
    try {
      setIsUploading(true);
      const form = evt.currentTarget; // the <form> element
      const data = new FormData(form);
      await fetch(awsKeys.action, { method: 'POST', body: data });
      toast.success('Photo uploaded successfully.');
      setPhotoUploaded(true);
    } catch (err) {
      toast.error('Something went wrong while uploading Photo.');
    } finally {
      setIsUploading(false);
    }
  };

  const copyCardMetaIntoForm = (values, setFieldValue) => {
    const merged = { ...values.meta, ...existingCard.meta };
    setFieldValue('meta', merged);
    setShowExistingCard(false);
  };

  const handleSubmit = async (values) => {
    const payload = {
      name: values.name,
      price: Number(values.price),
      months: Number(values.months),
      includes: values.includes,
      meta: values.meta,
      icon: values.icon,
      photoUploaded,
      id: values.id,
    };

    if (isEdit) {
      await subscriptionInfo.editSubscription(payload);
    } else {
      await subscriptionInfo.createSubscription(payload);
    }
  };

  const isReadOnly = accessMode === 'view';
  const isCreating = subscriptionInfo.isCreating;

  return (
    <div className="create-subscription">
      <Formik
        enableReinitialize
        initialValues={initialFormikValues}
        validationSchema={Validation}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <>
            <section className="well create-artist col-md-6">
              <Form>
                <div className="row">
                  {!values.id && (
                    <div className="notice">
                      * Features addition option will be available after the subscription is created
                    </div>
                  )}

                  <div className="col-md-3">
                    <label>Name</label>
                  </div>
                  <div className="col-md-9">
                    <Field
                      name="name"
                      placeholder="Name"
                      type="text"
                      className={`form-control ${isReadOnly ? 'disabled' : ''}`}
                      disabled={isReadOnly}
                    />
                    {touched.name && errors.name ? <div className="field-error">{errors.name}</div> : null}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label>Months</label>
                  </div>
                  <div className="col-md-9">
                    <Field
                      name="months"
                      placeholder="Months"
                      type="number"
                      className={`form-control ${isReadOnly ? 'disabled' : ''}`}
                      disabled={isReadOnly}
                    />
                    {touched.months && errors.months ? <div className="field-error">{errors.months}</div> : null}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label>Price</label>
                  </div>
                  <div className="col-md-9">
                    <Field
                      name="price"
                      placeholder="Price"
                      type="number"
                      className={`form-control ${isReadOnly ? 'disabled' : ''}`}
                      disabled={isReadOnly}
                    />
                    {touched.price && errors.price ? <div className="field-error">{errors.price}</div> : null}
                  </div>
                </div>

                {subscriptionInfo.initialValues && (
                  <div className="row">
                    <div className="col-md-3">
                      <label>Features</label>
                    </div>
                    <div className="col-md-8">
                      <MultiSelect
                        options={features_list.map((f) => ({ label: f.name, value: f.id }))}
                        value={values.features}
                        onChange={(newVal) => handleFeaturesChange(newVal, values, setFieldValue)}
                        hasSelectAll={false}
                        labelledBy={'Select features'}
                        disableSearch={true}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-md-3">
                    <label>Description</label>
                  </div>
                  <div className="col-md-8">
                    <FieldArray name="includes">
                      {({ push, remove }) => (
                        <>
                          {(values.includes || []).map((inc, i) => (
                            <div className="include-box" key={i}>
                              <Field
                                name={`includes.${i}`}
                                placeholder="Includes"
                                type="text"
                                className={`form-control ${isReadOnly ? 'disabled' : ''}`}
                                disabled={isReadOnly}
                              />
                              {!isReadOnly && <span onClick={() => remove(i)}>&times;</span>}
                            </div>
                          ))}
                          {accessMode === 'edit' && (
                            <div className="col-md-1">
                              <div onClick={() => push('')}>
                                <i className="fa fa-icon fa-plus" />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </FieldArray>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label>Select Card</label>
                  </div>
                  <div className="col-md-9">
                    <select
                      defaultValue={JSON.stringify(cards[0])}
                      onChange={(e) => {
                        const card = JSON.parse(e.target.value);
                        setExistingCard(card);
                        setShowExistingCard(true);
                      }}
                      className={`form-control ${isReadOnly ? 'disabled' : ''}`}
                      disabled={isReadOnly}
                    >
                      {cards.map((card, ind) =>
                        !ind ? (
                          <option disabled key={ind} value={JSON.stringify(card)}>
                            {card.title}
                          </option>
                        ) : (
                          <option key={ind} value={JSON.stringify(card)}>
                            {card.title}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label>Card Colours</label>
                  </div>
                  <div className="col-md-8 d-flex-col">
                    {Object.keys(values.meta || {}).map((key, ind) => (
                      <div key={ind}>
                        <span className="capitalize">{key.split('_').join(' ')}</span>
                        <input
                          name={`meta.${key}`}
                          type="color"
                          value={values.meta[key] || ''}
                          onChange={(e) => setFieldValue(`meta.${key}`, e.target.value)}
                          className={`form-control ${isReadOnly ? 'disabled' : ''}`}
                          disabled={isReadOnly}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {isEdit ? (
                  <div className="row">
                    <div className="col-md-3">
                      <label>Photo</label>
                    </div>
                    <div className="col-md-9">{values.icon}</div>
                  </div>
                ) : null}

                {accessMode === 'edit' && (
                  <>
                    <div className="row">
                      <div className="col-md-3">
                        <label>Photo</label>
                      </div>
                      <div className="col-md-9">
                        {/* S3 presigned POST form */}
                        <form method="post" encType="multipart/form-data" onSubmit={uploadPhoto}>
                          <input type="hidden" name="key" value={awsKeys.key || 'NA'} />
                          <input type="hidden" name="acl" value={awsKeys.acl || 'NA'} />
                          <input type="hidden" name="X-Amz-Credential" value={awsKeys['x-amz-credential'] || 'NA'} />
                          <input type="hidden" name="X-Amz-Algorithm" value={awsKeys['x-amz-algorithm'] || 'NA'} />
                          <input type="hidden" name="X-Amz-Date" value={awsKeys['x-amz-date'] || 'NA'} />
                          <input type="hidden" name="Policy" value={awsKeys.policy || 'NA'} />
                          <input type="hidden" name="X-Amz-Signature" value={awsKeys['x-amz-signature'] || 'NA'} />
                          <input
                            type="file"
                            name="file"
                            onChange={(e) => onFileChange(e, setFieldValue)}
                            disabled={isReadOnly}
                          />{' '}
                          <br />
                          <input
                            type="submit"
                            name="submit"
                            value="Upload to Amazon S3"
                            className="btn btn-default btn-upload"
                            disabled={!disableUpload}
                          />
                        </form>
                      </div>
                    </div>

                    <div className="create-piece__btn-container">
                      <button
                        className="cus-btn-primary btn-block btn-lg"
                        type="submit"
                        disabled={isCreating || !values.name}
                      >
                        {isCreating || isUploading ? (
                          <span className="spinner" />
                        ) : (
                          <span>{pagemode === 'EDIT' ? 'Update' : 'Create'}</span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </Form>
            </section>

            <div>
              <div className="preview-card">
                <SubscriptionCard
                  meta={values.meta}
                  name={values.name}
                  months={values.months}
                  price={values.price}
                  includes={values.includes}
                />

                {showExistingCard && (
                  <div className="prev">
                    <div className="preview-text">
                      <div>{existingCard.title}</div>
                      <i className="fas fa-times" onClick={() => setShowExistingCard(false)}></i>
                    </div>
                    <div className="add-colors" onClick={() => copyCardMetaIntoForm(values, setFieldValue)}>
                      Add Colours to current card
                    </div>
                    <SubscriptionCard meta={existingCard.meta} />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  );
}
